import React, { useState, useEffect } from "react";
import MerchantEnrollmentAPI from "../../api/MerchantEnrollmentAPI";
import PlacesAutoCompleteInput from "../PlacesAutoCompleteInput";
import PhoneNumber from "libphonenumber-js";

function MerchantInformationForm({ handleNext, payload, setPayload }) {
  const [hasDelivery, setHasDelivery] = useState(false);

  const [data, setData] = useState({
    MerchantName: "",
    MerchantType: "offline",
    WebsiteProviderID: 0,
    AllowTips: true,
    DistrictID: "",
    CityID: "",
    Address: "",
    PhoneNumber: "",
    MobileNumber: "",
    DeliveryNumber: "",
  });

  const isVisited =
    Object.keys(payload.MerchantEnrollmentInformation).length !== 0;

  useEffect(() => {
    if (isVisited) {
      setData(payload.MerchantEnrollmentInformation);
      setAdditionals(payload.Additionals);
      setHasDelivery(!!payload.MerchantEnrollmentInformation.DeliveryNumber);
    }
  }, []);

  const [errors, setErrors] = useState({
    MerchantName: "",
    WebsiteProviderID: "",
    DistrictID: "",
    CityID: "",
    Address: "",
    PhoneNumber: "",
    MobileNumber: "",
    DeliveryNumber: "",
  });

  const [additionals, setAdditionals] = useState({
    District: "",
    City: "",
  });

  const {
    GetDistricts,
    GetCities,
    GetWebsiteProviders,
    districtsList,
    citiesList,
    webProvidersList,
  } = MerchantEnrollmentAPI();

  useEffect(() => {
    GetDistricts();
    GetWebsiteProviders();
  }, []);

  useEffect(() => {
    if (data.DistrictID) {
      if (!isVisited) setData({ ...data, CityID: "" });
      GetCities(data.DistrictID);
    }
  }, [data.DistrictID]);

  useEffect(() => {
    if (data.DistrictID && districtsList)
      setAdditionals({
        ...additionals,
        District: districtsList.find((x) => x.value === Number(data.DistrictID))
          ?.label,
      });
  }, [data.DistrictID]);

  useEffect(() => {
    if (data.CityID && citiesList)
      setAdditionals({
        ...additionals,
        City: citiesList.find((x) => x.value === Number(data.CityID))?.label,
      });
  }, [data.CityID]);

  useEffect(() => {
    if (data.WebsiteProviderID && webProvidersList)
      setAdditionals({
        ...additionals,
        WebsiteProvider: webProvidersList.find(
          (x) => x.value === Number(data.WebsiteProviderID)
        )?.label,
      });
  }, [data.WebsiteProviderID]);

  useEffect(() => {
    const handleWheel = (event) => {
      event.preventDefault();
    };

    const inputElement = document.getElementById("number-input");
    inputElement.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      inputElement.removeEventListener("wheel", handleWheel);
    };
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setData({ ...data, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const handleDeliveryRadioChange = (event) => {
    const { id } = event.target;
    setHasDelivery(id === "del-yes");
    if (id === "del-no") setData({ ...data, DeliveryNumber: "" });
  };

  const handleTypeRadioChange = (event) => {
    const { id } = event.target;
    setData({ ...data, MerchantType: id });
    if (id === "offline")
      setData({ ...data, WebsiteProviderID: "0", MerchantType: "offline" });
  };

  const handleTipsRadioChange = (event) => {
    const { id } = event.target;
    setData({ ...data, AllowTips: id === "tips-yes" });
  };

  const validate = () => {
    let temp = {};
    temp.MerchantName = data.MerchantName.trim()
      ? ""
      : "Merchant name is required.";
    temp.MerchantType = data.MerchantType ? "" : "Merchant type is required.";
    temp.WebsiteProviderID =
      data.WebsiteProviderID || data.MerchantType === "offline"
        ? ""
        : "Website provider is required.";
    temp.DistrictID = data.DistrictID ? "" : "District is required.";
    temp.CityID = data.CityID ? "" : "City is required.";
    temp.Address = data.Address.trim() ? "" : "Address is required.";

    temp.PhoneNumber = data.PhoneNumber ? "" : "Phone number is required.";
    if (data.PhoneNumber) {
      const parsedPhoneNumber = PhoneNumber(data.PhoneNumber, "LB");
      temp.PhoneNumber = parsedPhoneNumber.isValid()
        ? temp.PhoneNumber
        : "Phone number is invalid.";
    }

    temp.MobileNumber = data.MobileNumber ? "" : "Mobile number is required.";
    if (data.MobileNumber) {
      const parsedPhoneNumber = PhoneNumber(data.MobileNumber, "LB");
      temp.MobileNumber = parsedPhoneNumber.isValid()
        ? temp.MobileNumber
        : "Mobile number is invalid.";
    }

    temp.DeliveryNumber =
      data.DeliveryNumber || !hasDelivery ? "" : "Delivery number is required.";
    if (data.DeliveryNumber && hasDelivery) {
      const parsedPhoneNumber = PhoneNumber(data.DeliveryNumber, "LB");
      temp.DeliveryNumber = parsedPhoneNumber.isValid()
        ? temp.DeliveryNumber
        : "Delivery number is invalid.";
    }

    setErrors(temp);
    return Object.values(temp).every((x) => x === "");
  };

  const handleContinue = () => {
    if (validate()) {
      setPayload({
        ...payload,
        MerchantEnrollmentInformation: data,
        Additionals: {
          ...payload.Additionals,
          District: additionals.District,
          City: additionals.City,
        },
      });
      handleNext();
    }
  };

  return (
    <>
      <div className="form-container">
        <div className="form-title">MERCHANT INFORMATION</div>

        <div className="form-group">
          <div className="form-label">Merchant Name</div>
          <input
            type="text"
            className="form-input"
            placeholder="Merchant Name"
            name="MerchantName"
            onChange={handleChange}
            value={data.MerchantName}
          />
          {errors.MerchantName && (
            <div className="error">{errors.MerchantName}</div>
          )}
        </div>

        <div className="form-group">
          <div className="form-label">Merchant Type</div>
          <div className="radio-group">
            <div className="radio-cont">
              <label htmlFor="offline">Physical ONLY</label>
              <input
                type="radio"
                name="merchant-type"
                id="offline"
                checked={data.MerchantType === "offline"}
                onChange={handleTypeRadioChange}
              />
            </div>
            <div className="radio-cont">
              <label htmlFor="online">Online ONLY</label>
              <input
                type="radio"
                name="merchant-type"
                id="online"
                onChange={handleTypeRadioChange}
                checked={data.MerchantType === "online"}
              />
            </div>
            <div className="radio-cont">
              <label htmlFor="both">Both</label>
              <input
                type="radio"
                name="merchant-type"
                id="both"
                onChange={handleTypeRadioChange}
                checked={data.MerchantType === "both"}
              />
            </div>
          </div>
        </div>
        {(data.MerchantType === "online" || data.MerchantType === "both") && (
          <div className="form-group">
            <div className="form-label">Website Provider</div>
            <select
              id="website-provider"
              className="form-input"
              onChange={handleChange}
              name="WebsiteProviderID"
            >
              <option value="0">
                {payload.MerchantEnrollmentInformation?.WebsiteProviderID
                  ? additionals.WebsiteProvider
                  : "Select"}
              </option>
              {webProvidersList &&
                webProvidersList.map(({ label, value }, index) => (
                  <option key={index} value={value}>
                    {label}
                  </option>
                ))}
            </select>
            {errors.WebsiteProviderID && (
              <div className="error">{errors.WebsiteProviderID}</div>
            )}
          </div>
        )}

        <div className="form-group">
          <div className="form-label">Enable Tips Feature</div>
          <div className="radio-group">
            <div className="radio-cont">
              <label htmlFor="tips-yes">Yes</label>
              <input
                type="radio"
                name="tips-features"
                id="tips-yes"
                checked={data.AllowTips}
                onChange={handleTipsRadioChange}
              />
            </div>
            <div className="radio-cont">
              <label htmlFor="tips-no">No</label>
              <input
                type="radio"
                name="tips-features"
                id="tips-no"
                checked={!data.AllowTips}
                onChange={handleTipsRadioChange}
              />
            </div>
          </div>
        </div>

        <div className="input-group">
          <div className="form-group">
            <div className="form-label">District</div>
            <select
              name="DistrictID"
              id="district"
              className="form-input"
              onChange={handleChange}
            >
              <option value="0">
                {additionals.District && isVisited
                  ? additionals.District
                  : "Select"}
              </option>
              {districtsList &&
                districtsList.map((district, index) => (
                  <option key={index} value={district.value}>
                    {district.label}
                  </option>
                ))}
            </select>
            {errors.DistrictID && (
              <span className="error">{errors.DistrictID}</span>
            )}
          </div>
          <div className="form-group">
            <div className="form-label">City</div>
            <select
              name="CityID"
              id="city"
              className="form-input"
              onChange={handleChange}
              disabled={!data.DistrictID}
              value={data.DistrictID ? data.CityID : 0}
            >
              <option value="0">
                {additionals.City && isVisited ? additionals.City : "Select"}
              </option>
              {citiesList &&
                citiesList.map((city, index) => (
                  <option key={index} value={city.value}>
                    {city.label}
                  </option>
                ))}
            </select>
            {errors.CityID && <span className="error">{errors.CityID}</span>}
          </div>
        </div>

        <div className="form-group">
          <div className="form-label">Address</div>
          <div className="form-address">
            <PlacesAutoCompleteInput
              data={data}
              setData={setData}
              objKey="Address"
            />
          </div>

          {/*<div className="input-pin">*/}
          {/*    <input type="text" className="form-address" placeholder="Street, building, city, country"*/}
          {/*           name="Address" onChange={handleChange}/>*/}
          {/*    <img src="/Images/pin.png" className="address-pin" alt=""/>*/}
          {/*</div>*/}
          {errors.Address && <span className="error">{errors.Address}</span>}
        </div>

        <div className="input-group">
          <div className="form-group">
            <div className="form-label">Phone Number</div>
            <input
              id="number-input"
              type="number"
              className="form-input"
              name="PhoneNumber"
              onChange={handleChange}
              value={data.PhoneNumber}
            />
            {errors.PhoneNumber && (
              <span className="error">{errors.PhoneNumber}</span>
            )}
          </div>
          <div className="form-group">
            <div className="form-label">Mobile Number</div>
            <input
              id="number-input"
              type="number"
              className="form-input"
              name="MobileNumber"
              onChange={handleChange}
              value={data.MobileNumber}
            />
            {errors.MobileNumber && (
              <span className="error">{errors.MobileNumber}</span>
            )}
          </div>
        </div>

        <div className="form-group">
          <div className="form-label">Has Delivery</div>
          <div className="radio-group">
            <div className="radio-cont">
              <label htmlFor="del-yes">Yes</label>
              <input
                type="radio"
                name="has-delivery"
                id="del-yes"
                onChange={handleDeliveryRadioChange}
                defaultChecked={
                  hasDelivery ||
                  !!payload.MerchantEnrollmentInformation?.DeliveryNumber
                }
              />
            </div>
            <div className="radio-cont">
              <label htmlFor="del-no">No</label>
              <input
                type="radio"
                name="has-delivery"
                id="del-no"
                onChange={handleDeliveryRadioChange}
                defaultChecked={
                  !payload.MerchantEnrollmentInformation?.DeliveryNumber
                }
              />
            </div>
          </div>
        </div>
        {hasDelivery && (
          <div className="form-group">
            <div className="form-label">Delivery Number</div>
            <input
              type="number"
              className="form-input"
              name="DeliveryNumber"
              onChange={handleChange}
              value={data.DeliveryNumber}
            />
            {errors.DeliveryNumber && (
              <div className="error">{errors.DeliveryNumber}</div>
            )}
          </div>
        )}
      </div>
      <button onClick={handleContinue} className="continue-btn">
        Continue
      </button>
    </>
  );
}

export default MerchantInformationForm;
